<template>
    <div v-if="authStore.isAuthenticated">
        <div
            v-if="hasImportAccess"
            class="module import-classement"
            :class="{ wide: step === 'rows' || step === 'preview' }"
        >
            <h2 class="page-title">Import de classement</h2>
            <p v-if="lockedScope" class="scope-banner">
                Import pour {{ lockedScope.level === 'comite' ? 'le comité' : 'la ligue' }}
                <strong>{{ lockedScope.name }}</strong>.
            </p>

            <StepSource v-if="step === 'source'" :allowFederal="isSuperAdmin" @select="onSelectSource" />

            <StepParams
                v-else-if="step === 'params'"
                :params="params"
                :lockedScope="lockedScope"
                @next="onParamsNext"
                @back="step = 'source'"
            />

            <StepRowsInput
                v-else-if="step === 'rows'"
                :rows="rows"
                :source="params.source!"
                :params="params"
                @next="step = 'preview'"
                @back="step = 'params'"
            />

            <StepPreview
                v-else-if="step === 'preview'"
                :params="params"
                :rows="rows"
                @next="onResult"
                @back="step = 'rows'"
            />

            <StepDartConnect
                v-else-if="step === 'dartconnect'"
                :params="params"
                @next="onResult"
                @back="step = 'params'"
            />

            <StepFederalPdf
                v-else-if="step === 'federal'"
                @next="onResult"
                @back="step = 'source'"
            />

            <StepResult
                v-else-if="step === 'result'"
                :params="params"
                :result="result ?? {}"
                @restart="onRestart"
            />
        </div>

        <div v-else class="centered-block">
            <p>Cette section est réservée aux représentants de comité, de ligue, et aux super administrateurs.</p>
            <router-link to="/" class="cta-link">← Retour à l'accueil</router-link>
        </div>
    </div>

    <div v-else class="centered-block">
        <h2>Connexion requise</h2>
        <p>Veuillez vous connecter pour accéder à cette section.</p>
        <button class="btn-primary" @click="authStore.openAuthModal">Connexion</button>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/useAuthStore'
import dartsHubApi from '@/axios/dartsHubApi'
import StepSource from './import-classement/StepSource.vue'
import StepParams from './import-classement/StepParams.vue'
import StepRowsInput from './import-classement/StepRowsInput.vue'
import StepPreview from './import-classement/StepPreview.vue'
import StepDartConnect from './import-classement/StepDartConnect.vue'
import StepFederalPdf from './import-classement/StepFederalPdf.vue'
import StepResult from './import-classement/StepResult.vue'
import { emptyWizardParams, type SourceKind, type ImportRow, type WizardParams, type LockedScope } from './import-classement/types'

const authStore = useAuthStore()
const isSuperAdmin = computed(() => authStore.scopes.includes('super_admin'))
const isComiteRep = computed(() => authStore.scopes.includes('comitee_representative'))
const isLigueRep = computed(() => authStore.scopes.includes('ligue_representative'))
// Restreint au super_admin pour l'instant côté client (demande du 2026-09-21) -- le
// backend (require_import_access) autorise toujours aussi comitee_representative/
// ligue_representative, cf. check_import_scope_access : seul l'accès à CETTE page est
// verrouillé ici, pas l'API.
const hasImportAccess = computed(() => isSuperAdmin.value)

// Un rep comité/ligue importe UNIQUEMENT dans sa propre entité (cf. check_import_scope_access
// côté serveur, qui refuserait de toute façon un autre comité/ligue) -- pas de sélection
// libre pour eux, contrairement au super_admin (`lockedScope` reste `null` pour lui).
const lockedScope = ref<LockedScope | null>(null)

onMounted(async () => {
    if (isSuperAdmin.value) return
    try {
        if (isComiteRep.value) {
            const { data } = await dartsHubApi.get('/clubs/my-comite')
            if (data) lockedScope.value = { level: 'comite', id: data.id, code: data.code, name: data.name }
        } else if (isLigueRep.value) {
            const { data } = await dartsHubApi.get('/clubs/my-ligue')
            if (data) lockedScope.value = { level: 'ligue', id: data.id, code: data.code, name: data.name }
        }
    } catch {
        lockedScope.value = null
    }
})

type Step = 'source' | 'params' | 'rows' | 'preview' | 'dartconnect' | 'federal' | 'result'

const step = ref<Step>('source')
const params = ref<WizardParams>(emptyWizardParams())
const rows = ref<ImportRow[]>([])
const result = ref<Record<string, unknown> | null>(null)

function onSelectSource(source: SourceKind) {
    if (source === 'federal_pdf' && !isSuperAdmin.value) return
    params.value.source = source
    // Le PDF fédéral n'a ni saison/périmètre/tournoi à choisir ici (le PDF donne déjà tout,
    // cf. StepFederalPdf) -- inutile de faire passer l'admin par StepParams pour rien.
    step.value = source === 'federal_pdf' ? 'federal' : 'params'
}

function onParamsNext() {
    step.value = params.value.source === 'dartconnect' ? 'dartconnect' : 'rows'
}

function onResult(r: Record<string, unknown>) {
    result.value = r
    step.value = 'result'
}

function onRestart() {
    params.value = emptyWizardParams()
    rows.value = []
    result.value = null
    step.value = 'source'
}
</script>

<style scoped>
.import-classement {
    max-width: 960px;
}

/* Étapes à tableau large (résultats, aperçu : 8 colonnes dont noms de doublette). */
.import-classement.wide {
    max-width: 1400px;
}

.page-title {
    margin-bottom: 1.5rem;
}

.scope-banner {
    margin: -1rem 0 1.5rem;
    font-size: 0.9rem;
    color: var(--color-fg-muted, #888);
}

.centered-block {
    text-align: center;
    padding: 3rem 1rem;
}
</style>
